import type { ProjectId, SourceControlProviderKind } from "@cmmd-forge/contracts";
import { ListFilterIcon, RotateCcwIcon } from "lucide-react";

import { Button } from "../../components/ui/button";
import {
  Menu,
  MenuGroup,
  MenuGroupLabel,
  MenuItem,
  MenuPopup,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator,
  MenuTrigger,
} from "../../components/ui/menu";
import type { PullRequestInboxError } from "./pullRequests.inbox.logic";
import { hasActivePullRequestFilters, type PullRequestsSearch } from "./pullRequests.logic";
import { cn } from "~/lib/utils";
import { RESPONSIVE_ICON_BUTTON_CLASS } from "~/lib/touchTarget";

interface PullRequestsFilterMenuProps {
  readonly search: PullRequestsSearch;
  readonly hostOptions: ReadonlyArray<string>;
  readonly projectOptions: ReadonlyArray<{ readonly id: ProjectId; readonly label: string }>;
  readonly unavailableErrors: ReadonlyArray<PullRequestInboxError>;
  readonly onSearchChange: (search: PullRequestsSearch) => void;
  readonly onReset: () => void;
}

export function PullRequestsFilterMenu(props: PullRequestsFilterMenuProps) {
  const { search } = props;
  const visibleHosts = includeSelectedOption(props.hostOptions, search.host, (host) => host);
  const visibleProjects = includeSelectedOption(
    props.projectOptions,
    search.projectId,
    (project) => project.id,
    (projectId) => ({ id: projectId as ProjectId, label: `Selected project (${projectId})` }),
  );
  const active = hasActivePullRequestFilters(search);
  return (
    <Menu>
      <MenuTrigger
        render={
          <Button
            size="icon"
            variant="outline"
            aria-label="Filter pull requests"
            data-active={active ? "true" : "false"}
            className={cn(
              "relative shrink-0",
              RESPONSIVE_ICON_BUTTON_CLASS,
              active ? "[--control-icon-color:currentColor]" : null,
            )}
          >
            <ListFilterIcon aria-hidden="true" className="size-4" />
            {active ? (
              <span
                aria-hidden="true"
                className="absolute top-1 right-1 size-1.5 rounded-full bg-primary"
              />
            ) : null}
          </Button>
        }
      />
      <MenuPopup align="end" className="w-64">
        <PullRequestsFilterGroup
          label="State"
          value={search.state}
          options={[
            ["open", "Open"],
            ["closed", "Closed"],
            ["merged", "Merged"],
            ["all", "All states"],
          ]}
          onChange={(state) =>
            props.onSearchChange({ ...search, state: state as PullRequestsSearch["state"] })
          }
        />
        <MenuSeparator />
        <PullRequestsFilterGroup
          label="Sort"
          value={search.sort ?? "updated"}
          options={[
            ["updated", "Recently updated"],
            ["ready", "Ready to merge"],
            ["blocked", "Blocked"],
            ["newest", "Newest"],
            ["oldest", "Oldest"],
            ["largest", "Largest"],
            ["smallest", "Smallest"],
          ]}
          onChange={(value) =>
            updateSearchField(
              search,
              "sort",
              value === "updated" ? "" : value,
              props.onSearchChange,
            )
          }
        />
        <MenuSeparator />
        <PullRequestsFilterGroup
          label="Involvement"
          value={search.involvement ?? "all"}
          options={[
            ["all", "All"],
            ["reviewing", "Reviewing"],
            ["authored", "Authored"],
          ]}
          onChange={(involvement) =>
            updateSearchField(
              search,
              "involvement",
              involvement === "all" ? "" : involvement,
              props.onSearchChange,
            )
          }
        />
        <MenuSeparator />
        <PullRequestsFilterGroup
          label="Draft"
          value={search.draft ?? "all"}
          options={[
            ["all", "All"],
            ["only", "Drafts only"],
            ["hide", "Hide drafts"],
          ]}
          onChange={(value) =>
            updateSearchField(search, "draft", value === "all" ? "" : value, props.onSearchChange)
          }
        />
        <MenuSeparator />
        <PullRequestsFilterGroup
          label="Review"
          value={search.review ?? "all"}
          options={[
            ["all", "All"],
            ["review-required", "Review required"],
            ["changes-requested", "Changes requested"],
            ["approved", "Approved"],
            ["none", "No review"],
          ]}
          onChange={(value) =>
            updateSearchField(search, "review", value === "all" ? "" : value, props.onSearchChange)
          }
        />
        <MenuSeparator />
        <PullRequestsFilterGroup
          label="Checks"
          value={search.checks ?? "all"}
          options={[
            ["all", "All"],
            ["passing", "Passing"],
            ["failing", "Failing"],
          ]}
          onChange={(value) =>
            updateSearchField(search, "checks", value === "all" ? "" : value, props.onSearchChange)
          }
        />
        <MenuSeparator />
        <PullRequestsFilterGroup
          label="Host"
          value={search.host ?? "all"}
          options={[["all", "All hosts"], ...visibleHosts.map((host) => [host, host] as const)]}
          onChange={(value) =>
            updateSearchField(search, "host", value === "all" ? "" : value, props.onSearchChange)
          }
        />
        <MenuSeparator />
        <PullRequestsFilterGroup
          label="Project"
          value={search.projectId ?? "all"}
          options={[
            ["all", "All projects"],
            ...visibleProjects.map((project) => [project.id, project.label] as const),
          ]}
          onChange={(value) =>
            updateSearchField(
              search,
              "projectId",
              value === "all" ? "" : (value as ProjectId),
              props.onSearchChange,
            )
          }
        />
        <MenuSeparator />
        <PullRequestsFilterGroup
          label="Provider"
          value={search.provider ?? "all"}
          options={[
            ["all", "All providers"],
            ["github", "GitHub"],
            ["gitlab", "GitLab"],
            ["bitbucket", "Bitbucket"],
            ["azure-devops", "Azure DevOps"],
          ]}
          onChange={(value) =>
            updateSearchField(
              search,
              "provider",
              value === "all" ? "" : (value as SourceControlProviderKind),
              props.onSearchChange,
            )
          }
        />
        {props.unavailableErrors.length > 0 ? (
          <UnavailableRepositories errors={props.unavailableErrors} />
        ) : null}
        {active ? (
          <>
            <MenuSeparator />
            <MenuItem onClick={props.onReset}>
              <RotateCcwIcon aria-hidden="true" className="size-4" />
              Reset filters
            </MenuItem>
          </>
        ) : null}
      </MenuPopup>
    </Menu>
  );
}

function PullRequestsFilterGroup(props: {
  readonly label: string;
  readonly value: string;
  readonly options: ReadonlyArray<readonly [string, string]>;
  readonly onChange: (value: string) => void;
}) {
  return (
    <MenuRadioGroup value={props.value} onValueChange={(value) => value && props.onChange(value)}>
      <MenuGroupLabel>{props.label}</MenuGroupLabel>
      {props.options.map(([value, label]) => (
        <MenuRadioItem key={value} value={value}>
          {label}
        </MenuRadioItem>
      ))}
    </MenuRadioGroup>
  );
}

function UnavailableRepositories(props: { readonly errors: ReadonlyArray<PullRequestInboxError> }) {
  return (
    <>
      <MenuSeparator />
      <MenuGroup>
        <MenuGroupLabel>Unavailable repositories</MenuGroupLabel>
        {props.errors.map((error) => (
          <MenuItem key={`${error.environmentId}:${error.message}`} disabled title={error.message}>
            <span className="line-clamp-2">Command repository unavailable: {error.message}</span>
          </MenuItem>
        ))}
      </MenuGroup>
    </>
  );
}

function includeSelectedOption<Option, Value>(
  options: ReadonlyArray<Option>,
  selected: Value | undefined,
  valueOf: (option: Option) => Value,
  fallback: (value: Value) => Option = (value) => value as unknown as Option,
): ReadonlyArray<Option> {
  return selected === undefined || options.some((option) => valueOf(option) === selected)
    ? options
    : [...options, fallback(selected)];
}

function updateSearchField(
  search: PullRequestsSearch,
  key: keyof PullRequestsSearch,
  value: unknown,
  onSearchChange: (next: PullRequestsSearch) => void,
): void {
  const remaining: Partial<PullRequestsSearch> = { ...search };
  delete remaining[key];
  onSearchChange(
    value === ""
      ? { state: search.state, ...remaining }
      : ({ ...remaining, [key]: value } as PullRequestsSearch),
  );
}
